import { supabase } from "@/integrations/supabase/client";
import { AdZone, AdZoneFormData, AdZoneKey } from "@/types/adZones";
import { normalizeFooterCards, FOOTER_GUIDES_SECTION_TITLE } from "@/utils/adZoneFooterUtils";

export async function fetchAdZones(includeInactive = false): Promise<AdZone[]> {
  let query = supabase.from("ad_zones" as "checklists").select("*").order("zone_key");

  if (!includeInactive) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query;

  if (error) {
    console.error("[adZoneService] fetchAdZones error:", error);
    return [];
  }

  return (data ?? []) as unknown as AdZone[];
}

export async function upsertAdZone(
  zoneKey: AdZoneKey,
  formData: AdZoneFormData,
  userId: string
): Promise<{ data: AdZone | null; error: Error | null }> {
  const payload: Record<string, unknown> = {
    zone_key: zoneKey,
    title: zoneKey === "footer" ? FOOTER_GUIDES_SECTION_TITLE : formData.title.trim(),
    description: formData.description.trim(),
    image_url: formData.image_url?.trim() || null,
    cta_label: formData.cta_label.trim() || null,
    cta_url: formData.cta_url.trim() || null,
    is_active: formData.is_active,
    updated_at: new Date().toISOString(),
    updated_by: userId,
  };

  if (zoneKey === "footer" && formData.cards) {
    payload.cards = normalizeFooterCards(formData.cards);
  }

  const { data, error } = await supabase
    .from("ad_zones" as "checklists")
    .upsert(payload, { onConflict: "zone_key" })
    .select()
    .single();

  if (error) {
    return { data: null, error: new Error(error.message) };
  }

  return { data: data as unknown as AdZone, error: null };
}

export async function uploadAdZoneImage(
  zoneKey: AdZoneKey,
  file: File,
  cardIndex?: number
): Promise<{ url: string | null; error: Error | null }> {
  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const cardPath = cardIndex !== undefined ? `/card-${cardIndex}` : "";
  const filePath = `ad-zones/${zoneKey}${cardPath}/${Date.now()}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from("blog-images")
    .upload(filePath, file, { upsert: true, contentType: file.type });

  if (uploadError) {
    return { url: null, error: new Error(uploadError.message) };
  }

  const { data } = supabase.storage.from("blog-images").getPublicUrl(filePath);
  return { url: data.publicUrl, error: null };
}
